import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useMemo } from "react";

import { $gitBranchAheadBehind, type GitBranchAheadBehindInput } from "../../internal/git";

/**
 * Hook to get the ahead/behind commit lists for two branches.
 * @returns The full ahead/behind data with commit lists, or undefined if not available
 */
export const useGitBranchAheadBehind = (documentId: string, source: string, target: string) => {
	const input: GitBranchAheadBehindInput = useMemo(
		() => ({ documentId, source, target }),
		[documentId, source, target]
	);
	const aheadBehindAtom = $gitBranchAheadBehind(input);
	const branchAheadBehindLoadable = useAtomValue(loadable(aheadBehindAtom));
	const refresh = useSetAtom(aheadBehindAtom);

	const data =
		branchAheadBehindLoadable.state === "hasData" ? branchAheadBehindLoadable.data : undefined;

	// Strip contiguous empty-description (draft) commits from the tip of the ahead list.
	// These are undescribed JJ working copy commits that aren't publishable.
	const publishableAhead = useMemo(() => {
		if (!data) {
			return [];
		}
		const ahead = data.ahead;
		let firstDescribed = 0;
		while (firstDescribed < ahead.length && !ahead[firstDescribed].message.trim()) {
			firstDescribed++;
		}
		return ahead.slice(firstDescribed);
	}, [data]);

	return {
		data,
		aheadCount: publishableAhead.length,
		behindCount: data?.behind.length ?? 0,
		publishableAhead,
		refresh,
		isLoading: branchAheadBehindLoadable.state === "loading",
		isError: branchAheadBehindLoadable.state === "hasError",
	};
};
